// ============================================
// SUPERVISION BAR COMPONENT
// Real-time stats display for managers
// Extracted from POSPageV2.jsx
// ============================================

import React from 'react';
import { 
    Settings, AlertTriangle, AlertCircle, CheckCircle,
    CreditCard, Flame, ChefHat, Activity, LayoutGrid,
    Monitor, Clock, TrendingUp
} from 'lucide-react';
import { cn } from '../../lib/utils';

const SupervisionBar = ({ stats, recentActivity, isManager, onOpenSettings }) => {
    if (!isManager || !stats) return null;
    
    const formatTime = (isoTime) => {
        if (!isoTime) return '';
        const date = new Date(isoTime);
        return date.toLocaleTimeString('fr-FR', { 
            timeZone: 'Africa/Libreville',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    // Get color classes based on status
    const getStatusColors = (status) => {
        switch (status) {
            case 'red': return {
                bg: 'bg-red-500/20',
                border: 'border-red-500/50',
                icon: 'text-red-400',
                value: 'text-red-400',
                pulse: true
            };
            case 'orange': return {
                bg: 'bg-orange-500/20',
                border: 'border-orange-500/50',
                icon: 'text-orange-400',
                value: 'text-orange-400',
                pulse: false
            };
            default: return {
                bg: 'bg-slate-700/50',
                border: 'border-transparent',
                icon: 'text-green-400',
                value: 'text-green-400',
                pulse: false
            };
        }
    };

    // Global status badge
    const getGlobalStatusBadge = () => {
        const status = stats.global_status || 'green';
        const message = stats.global_message || 'Service normal';
        
        if (status === 'red') {
            return (
                <div className="flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full animate-pulse">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-bold">{message}</span>
                </div>
            );
        } else if (status === 'orange') {
            return (
                <div className="flex items-center gap-2 bg-orange-500 text-white px-3 py-1 rounded-full">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-bold">{message}</span>
                </div>
            );
        } else {
            return (
                <div className="flex items-center gap-2 bg-green-600 text-white px-3 py-1 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-bold">{message}</span>
                </div>
            );
        }
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'payment': return <CreditCard className="w-3 h-3 text-green-400" />;
            case 'ready': return <CheckCircle className="w-3 h-3 text-blue-400" />;
            case 'cooking': return <Flame className="w-3 h-3 text-orange-400" />;
            case 'kitchen': return <ChefHat className="w-3 h-3 text-yellow-400" />;
            case 'delay': return <AlertTriangle className="w-3 h-3 text-red-400" />;
            default: return <Activity className="w-3 h-3 text-gray-400" />;
        }
    };

    const getActivityColor = (type, critical) => {
        if (critical) return 'border-l-red-500 bg-red-500/10';
        switch (type) {
            case 'payment': return 'border-l-green-500';
            case 'ready': return 'border-l-blue-500';
            case 'cooking': return 'border-l-orange-500';
            case 'kitchen': return 'border-l-yellow-500';
            case 'delay': return 'border-l-red-500 bg-red-500/10';
            default: return 'border-l-gray-500';
        }
    };

    // Get colors for each stat
    const tablesColors = getStatusColors(stats.tables_status);
    const caissesColors = getStatusColors(stats.caisses_status);
    const kitchenColors = getStatusColors(stats.kitchen_status);
    const readyColors = getStatusColors(stats.ready_status);
    const prepTimeColors = getStatusColors(stats.prep_time_status);
    const caColors = getStatusColors(stats.ca_status);

    return (
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white px-4 py-2 shadow-lg">
            {/* Top row: Global status badge */}
            <div className="flex items-center justify-between mb-2">
                {getGlobalStatusBadge()}
                
                {/* Settings button */}
                {onOpenSettings && (
                    <button 
                        onClick={onOpenSettings}
                        className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-700/50 transition-colors"
                        title="Paramètres supervision"
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                )}
            </div>
            
            <div className="flex items-center justify-between gap-4">
                {/* Stats Blocks */}
                <div className="flex items-center gap-2 flex-1 flex-wrap">
                    {/* Tables ouvertes */}
                    <div className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 min-w-[100px] border transition-all",
                        tablesColors.bg,
                        tablesColors.border,
                        tablesColors.pulse && "animate-pulse"
                    )}>
                        <LayoutGrid className={cn("w-4 h-4", tablesColors.icon)} />
                        <div>
                            <p className="text-xs text-slate-400">Tables</p>
                            <p className={cn("text-lg font-bold", tablesColors.value)}>{stats.tables_ouvertes || 0}</p>
                        </div>
                    </div>
                    
                    {/* Caisses actives */}
                    <div className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 min-w-[100px] border transition-all",
                        caissesColors.bg,
                        caissesColors.border,
                        caissesColors.pulse && "animate-pulse"
                    )}>
                        <Monitor className={cn("w-4 h-4", caissesColors.icon)} />
                        <div>
                            <p className="text-xs text-slate-400">Caisses</p>
                            <p className={cn("text-lg font-bold", caissesColors.value)}>
                                {stats.caisses_actives || 0}
                                {stats.caisses_errors > 0 && <span className="text-red-400 text-sm ml-1">!</span>}
                            </p>
                        </div>
                    </div>
                    
                    {/* Commandes en cuisine */}
                    <div className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 min-w-[100px] border transition-all",
                        kitchenColors.bg,
                        kitchenColors.border,
                        kitchenColors.pulse && "animate-pulse"
                    )}>
                        <ChefHat className={cn("w-4 h-4", kitchenColors.icon)} />
                        <div>
                            <p className="text-xs text-slate-400">En cuisine</p>
                            <p className={cn("text-lg font-bold", kitchenColors.value)}>{stats.commandes_cuisine || 0}</p>
                        </div>
                    </div>
                    
                    {/* Commandes prêtes */}
                    <div className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 min-w-[100px] border transition-all",
                        readyColors.bg,
                        readyColors.border,
                        (readyColors.pulse || stats.commandes_pretes > 0) && "animate-pulse"
                    )}>
                        <CheckCircle className={cn("w-4 h-4", readyColors.icon)} />
                        <div>
                            <p className="text-xs text-slate-400">Prêtes</p>
                            <p className={cn("text-lg font-bold", readyColors.value)}>{stats.commandes_pretes || 0}</p>
                        </div>
                    </div>
                    
                    {/* Temps moyen cuisine */}
                    <div className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 min-w-[100px] border transition-all",
                        prepTimeColors.bg,
                        prepTimeColors.border,
                        prepTimeColors.pulse && "animate-pulse"
                    )}>
                        <Clock className={cn("w-4 h-4", prepTimeColors.icon)} />
                        <div>
                            <p className="text-xs text-slate-400">Temps moy.</p>
                            <p className={cn("text-lg font-bold", prepTimeColors.value)}>
                                {stats.temps_moyen_cuisine || 0}
                                <span className="text-xs font-normal ml-0.5">min</span>
                            </p>
                        </div>
                    </div>
                    
                    {/* Chiffre d'affaires */}
                    <div className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 min-w-[140px] border transition-all",
                        caColors.bg,
                        caColors.border,
                        caColors.pulse && "animate-pulse"
                    )}>
                        <TrendingUp className={cn("w-4 h-4", caColors.icon)} />
                        <div>
                            <p className="text-xs text-slate-400">CA du jour</p>
                            <p className={cn("text-lg font-bold", caColors.value)}>{stats.chiffre_affaires_formatted || '0 F'}</p>
                        </div>
                    </div>
                </div>
                
                {/* Recent Activity Feed */}
                <div className="flex-1 max-w-md">
                    <div className="flex items-center gap-2 mb-1">
                        <Activity className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-400 font-medium">Activité en direct</span>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg overflow-hidden max-h-[52px]">
                        <div className="divide-y divide-slate-600/30">
                            {recentActivity && recentActivity.slice(0, 3).map((activity, idx) => (
                                <div 
                                    key={idx} 
                                    className={cn(
                                        "flex items-center gap-2 px-2 py-1 text-xs border-l-2",
                                        getActivityColor(activity.type, activity.critical)
                                    )}
                                >
                                    {getActivityIcon(activity.type)}
                                    <span className={cn(
                                        "flex-1 truncate",
                                        activity.critical ? "text-red-300 font-medium" : "text-slate-300"
                                    )}>{activity.message}</span>
                                    <span className="text-slate-500 text-[10px]">{formatTime(activity.time)}</span>
                                </div>
                            ))}
                            {(!recentActivity || recentActivity.length === 0) && (
                                <div className="px-2 py-2 text-xs text-slate-500 text-center">
                                    Aucune activité récente
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupervisionBar;
